import * as THREE from 'three';
import type { ResolvedStyle } from '../render/OverrideResolver';
import type { SfxRuntime } from '../render/Sfx';
import type { AppRenderer } from '../render/Renderer';

// Runtime-only debug toggles. NOT persisted to StyleConfig — flipping these
// must never write into StyleConfig.defaults.json. Used to isolate each
// lighting contributor so the user can build the scene up from black.
export interface DebugLayers {
  clearToBlack: boolean;
  sky: boolean;
  keyLight: boolean;
  fillLight: boolean;
  hemisphere: boolean;
  bounceLight: boolean;
  rimLight: boolean;
  environment: boolean;
  fog: boolean;
  emissive: boolean;
  bloom: boolean;
  colorGrade: boolean;
  motionBlur: boolean;
  toneMapping: boolean;
}

export function defaultDebugLayers(): DebugLayers {
  return {
    clearToBlack: false,
    sky: true,
    keyLight: true,
    fillLight: true,
    hemisphere: true,
    bounceLight: true,
    rimLight: true,
    environment: true,
    fog: true,
    emissive: true,
    bloom: true,
    colorGrade: true,
    motionBlur: true,
    toneMapping: true,
  };
}

export interface DebugLayerTargets {
  renderer: AppRenderer;
  scene: THREE.Scene;
  skyMesh: THREE.Object3D;
  keyLight: THREE.DirectionalLight;
  fillLight: THREE.DirectionalLight;
  hemisphere: THREE.HemisphereLight;
  bounceLight: THREE.DirectionalLight;
  rimLight: THREE.DirectionalLight;
  fog: THREE.FogExp2;
  emissiveGroups: THREE.Object3D[];
  bloom: SfxRuntime | null;
}

// Snapshot the IBL probe + fog when toggled off so we can reattach without
// fighting per-frame logic that legitimately sets these (e.g. top-down mode
// nulls fog in TrackReview). Module-scope: one scene per session.
let stashedEnvironment: THREE.Texture | null = null;
let stashedFog: THREE.Fog | THREE.FogExp2 | null = null;

// Idempotent: runs every frame after any style apply. Each toggle re-derives
// from `style` so flipping back on restores the styled value, never a stale
// snapshot. Order: lights → sky/fog/env → renderer state → post FX.
export function applyDebugLayers(
  layers: DebugLayers,
  style: ResolvedStyle,
  t: DebugLayerTargets,
): void {
  t.keyLight.intensity = layers.keyLight ? style.sunRig.key.intensity : 0;
  t.fillLight.intensity = layers.fillLight ? style.sunRig.fill.intensity : 0;
  t.hemisphere.intensity = layers.hemisphere ? style.sunRig.hemisphere.intensity : 0;
  t.bounceLight.intensity = layers.bounceLight ? style.sunRig.bounce.intensity : 0;
  t.rimLight.intensity = layers.rimLight ? style.sunRig.rim.intensity : 0;

  t.skyMesh.visible = layers.sky;
  for (const group of t.emissiveGroups) group.visible = layers.emissive;

  if (layers.environment) {
    if (t.scene.environment === null && stashedEnvironment !== null) {
      t.scene.environment = stashedEnvironment;
      stashedEnvironment = null;
    }
  } else if (t.scene.environment !== null) {
    stashedEnvironment = t.scene.environment;
    t.scene.environment = null;
  }

  if (layers.fog) {
    if (t.scene.fog === null && stashedFog !== null) {
      t.scene.fog = stashedFog;
      stashedFog = null;
    }
  } else if (t.scene.fog !== null) {
    stashedFog = t.scene.fog;
    t.scene.fog = null;
  }

  if (layers.clearToBlack) t.renderer.setClearColor(0x000000, 1);
  if (!layers.toneMapping) t.renderer.toneMapping = THREE.NoToneMapping;

  if (t.bloom) {
    t.bloom.setBloomEnabled(layers.bloom);
    t.bloom.setColorGradeEnabled(layers.colorGrade && style.sfx.colorGrade.enabled);
    t.bloom.setMotionBlurEnabled(layers.motionBlur);
    if (!layers.motionBlur) t.bloom.setRadialBlurStrength(0);
  }
}
